/**
 * Dashboard Configuration
 *
 * Handles environment-specific configuration including API endpoints.
 */

/**
 * One independent API stack per region. A new account picks its region at
 * sign-up (CeLoginPage); EU is the default and the first host tried. An
 * existing account is served from the region it lives in, which the API
 * tells us at sign-in (`use` in the send-code /
 * verify-code response). That host is remembered per browser as the
 * account's home and every later call goes there.
 *
 * Localhost exception: production API CORS does not allow
 * localhost origins. A persisted home pin of https://api.complyedge.io (or
 * eu) therefore makes every fetch fail as "Network error". On a local
 * origin we keep the pin in memory only for the in-request region hop and
 * never read/write localStorage for it.
 */
const PRODUCTION_API = 'https://eu.api.complyedge.io';
export const REGION_API_HOSTS = [
  'https://eu.api.complyedge.io',
  'https://api.complyedge.io',
] as const;
const HOME_API_STORAGE_KEY = 'complyedge-api-home';
/** In-memory pin so a region hop works even when localStorage is unavailable. */
let homeApiUrlMemory: string | undefined;
const PRODUCTION_WEBSITE = 'https://www.complyedge.io';
const PRODUCTION_TRUST = 'https://trust.complyedge.io';
/** Match CE_PORT_*_BASE in scripts/development/local-dev-ports.sh */
const LOCAL_API = 'http://localhost:18800';
const LOCAL_WEBSITE = 'http://localhost:18875';
const LOCAL_TRUST = 'http://localhost:18876';

function isLocalHost(): boolean {
  if (import.meta.env.MODE === 'test') {
    return false;
  }
  return isBrowserOnLocalOrigin();
}

/** True when the page origin is localhost, ignores vitest MODE=test bypass. */
export function isBrowserOnLocalOrigin(): boolean {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function envUrl(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  if (typeof value === 'string' && value.trim()) {
    return value.replace(/\/$/, '');
  }
  return undefined;
}

function isKnownRegionHost(value: string): boolean {
  return (REGION_API_HOSTS as readonly string[]).includes(value);
}

/** Host the signed-in account lives on, if sign-in told us. */
export function getHomeApiUrl(): string | undefined {
  if (homeApiUrlMemory && isKnownRegionHost(homeApiUrlMemory)) {
    return homeApiUrlMemory;
  }
  // Local dogfood: ignore sticky localStorage pins (prod CORS → Network error).
  if (isBrowserOnLocalOrigin()) {
    try {
      localStorage.removeItem(HOME_API_STORAGE_KEY);
    } catch {
      /* scrub best-effort */
    }
    return undefined;
  }
  try {
    const value = localStorage.getItem(HOME_API_STORAGE_KEY);
    return value && isKnownRegionHost(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Remember the account's home host. Only a known ComplyEdge region host is
 * accepted: the value comes from an API response body, and a response must
 * never be able to point the dashboard at an arbitrary origin.
 *
 * On localhost the pin stays in memory for the follow hop only, never
 * localStorage.
 */
export function setHomeApiUrl(host: string | undefined | null): boolean {
  const value = (host ?? '').replace(/\/$/, '');
  if (!isKnownRegionHost(value)) {
    return false;
  }
  homeApiUrlMemory = value;
  if (isBrowserOnLocalOrigin()) {
    try {
      localStorage.removeItem(HOME_API_STORAGE_KEY);
    } catch {
      /* scrub best-effort */
    }
    return true;
  }
  try {
    localStorage.setItem(HOME_API_STORAGE_KEY, value);
  } catch {
    /* storage unavailable: memory pin still steers this page load */
  }
  return true;
}

export function clearHomeApiUrl(): void {
  homeApiUrlMemory = undefined;
  try {
    localStorage.removeItem(HOME_API_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Get the API base URL based on environment
 *
 * Local development: http://localhost:18800 (or VITE_API_URL from ./run.sh)
 * Production: the signed-in account's home region host, else
 * https://eu.api.complyedge.io (the default region)
 *
 * Call this at request time, not at import time: the home host is learned
 * during sign-in, after the app has loaded.
 *
 * Home host wins everywhere, including localhost, once sign-in (or a
 * send-code region redirect) pins it. Otherwise local dogfood of a US-homed
 * account would re-hit the local stack forever and never deliver OTP
 *.
 */
export const getApiUrl = (): string => {
  const home = getHomeApiUrl();
  if (home) {
    return home;
  }
  return envUrl('VITE_API_URL') ?? (isLocalHost() ? LOCAL_API : PRODUCTION_API);
};

/** Default host at load time. Prefer getApiUrl() for requests. */
export const API_URL = getApiUrl();

/** Marketing site: docs, pricing, Enforcement Seal embed guide. */
export const getWebsiteUrl = (): string => {
  return envUrl('VITE_WEBSITE_URL') ?? (isLocalHost() ? LOCAL_WEBSITE : PRODUCTION_WEBSITE);
};

export const WEBSITE_URL = getWebsiteUrl();

/** Public trust portal (Enforcement Seal pages). */
export const getTrustBaseUrl = (): string => {
  return envUrl('VITE_TRUST_URL') ?? (isLocalHost() ? LOCAL_TRUST : PRODUCTION_TRUST);
};

export const TRUST_URL = getTrustBaseUrl();

export const publicTrustPageUrl = (slug: string): string =>
  `${TRUST_URL}/${encodeURIComponent(slug)}`;

/**
 * Homepage dogfood seal, used as “see how it looks”. Both URLs are pinned to
 * production on purpose: the example tenant lives only there, so resolving the
 * page through TRUST_URL sent local/dev dashboards to a 404 on the local trust
 * server (2026-09-19). The example must open wherever the dashboard runs.
 */
export const EXAMPLE_TRUST_SLUG = 'ivd';
export const EXAMPLE_TRUST_BADGE_URL = 'https://api.complyedge.io/v1/public/badge/ivd.svg';
export const EXAMPLE_TRUST_PAGE_URL = `${PRODUCTION_TRUST}/${EXAMPLE_TRUST_SLUG}`;

export const enforcementSealEmbedHtml = (slug: string, badgeSvgUrl: string): string =>
  `<a href="${PRODUCTION_TRUST}/${encodeURIComponent(slug)}" rel="noopener noreferrer">\n` +
  `  <img src="${badgeSvgUrl}" alt="ComplyEdge Enforcement Seal" height="26" />\n` +
  `</a>`;

export const config = {
  apiUrl: API_URL,
  websiteUrl: WEBSITE_URL,
  trustUrl: TRUST_URL,
  isDevelopment: isLocalHost(),
  isProduction: !isLocalHost(),
};
